<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button
                                        type="button"
                                        class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500 hover:bg-gray-50"
                                        @click="goBack"
                                >
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">
                                                {{ bulkDiscount?.name || 'Loading...' }}
                                        </h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Bulk Discount ID: {{ routeId }}</p>
                                </div>
                        </div>
                </header>

                <!-- Loading State -->
                <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading bulk discount...</span>
                </div>

                <!-- Main Content -->
                <main v-else class="p-6">
                        <!-- Tabs -->
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <!-- Tab Headers -->
                                <div class="flex border-b border-gray-200 overflow-x-auto">
                                        <button
                                                v-for="tab in tabs"
                                                :key="tab.id"
                                                type="button"
                                                class="py-4 px-6 text-sm font-medium whitespace-nowrap border-none bg-transparent cursor-pointer border-b-2 -mb-px"
                                                :class="activeTab === tab.id ? 'text-orange-7 border-orange-7 bg-orange-1' : 'text-gray-500 border-transparent'"
                                                @click="activeTab = tab.id"
                                        >
                                                {{ tab.label }}
                                        </button>
                                </div>

                                <!-- Tab Content -->
                                <div class="p-6">
                                        <!-- Properties Tab -->
                                        <div v-if="activeTab === 'properties'">
                                                <div class="flex justify-between items-center mb-4">
                                                        <h3 class="text-base font-semibold text-gray-900 m-0">Bulk Discount Properties</h3>
                                                        <div class="flex items-center gap-2">
                                                                <span
                                                                        v-if="hasChanges"
                                                                        class="text-xs bg-amber-100 text-amber-700 py-1 px-2 rounded-full font-medium"
                                                                >
                                                                        Unsaved changes
                                                                </span>
                                                                <button
                                                                        type="button"
                                                                        :disabled="!hasChanges || isSaving"
                                                                        class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-6 transition-colors"
                                                                        @click="saveChanges"
                                                                >
                                                                        <svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                                        </svg>
                                                                        <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                        {{ isSaving ? 'Saving...' : 'Save Changes' }}
                                                                </button>
                                                        </div>
                                                </div>
                                                <div class="max-w-lg">
                                                        <div class="mb-4">
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">ID</label>
                                                                <input
                                                                        :value="bulkDiscount?.id"
                                                                        type="text"
                                                                        disabled
                                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500"
                                                                >
                                                        </div>
                                                        <div class="mb-4">
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                                                <input
                                                                        v-model="editData.name"
                                                                        type="text"
                                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                        </div>
                                                        <div class="mb-4">
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Count Type</label>
                                                                <select
                                                                        v-model="editData.count"
                                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                                        <option value="pages">Pages</option>
                                                                        <option value="products">Products</option>
                                                                </select>
                                                        </div>
                                                        <div class="mb-4">
                                                                <div class="flex items-center gap-3">
                                                                        <button
                                                                                type="button"
                                                                                class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                                                                                :class="editData.active === 1 ? 'bg-orange-7' : 'bg-gray-300'"
                                                                                @click="editData.active = editData.active === 1 ? 0 : 1"
                                                                        >
                                                                                <span
                                                                                        class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm"
                                                                                        :class="editData.active === 1 ? 'translate-x-6' : 'translate-x-1'"
                                                                                ></span>
                                                                        </button>
                                                                        <div>
                                                                                <span class="text-sm font-medium text-gray-700">Active</span>
                                                                                <p class="text-xs text-gray-500 mt-0.5 mb-0">Enable this bulk discount for customers</p>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>

                                        <!-- Quantity Tiers Tab -->
                                        <div v-if="activeTab === 'quantities'">
                                                <div class="flex justify-between items-center mb-4">
                                                        <h3 class="text-base font-semibold text-gray-900 m-0">Quantity Tiers</h3>
                                                        <button
                                                                type="button"
                                                                class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer hover:bg-orange-6"
                                                                @click="openAddQuantityRow"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                                </svg>
                                                                Add Tier
                                                        </button>
                                                </div>

                                                <div v-if="quantities.length === 0" class="text-center py-12 px-4 bg-gray-50 rounded-lg">
                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                        </svg>
                                                        <p class="text-gray-500 text-sm m-0">No quantity tiers defined</p>
                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Add a tier to get started</p>
                                                </div>

                                                <div v-else class="overflow-x-auto">
                                                        <table class="w-full border-collapse">
                                                                <thead>
                                                                        <tr class="bg-gray-50">
                                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">From</th>
                                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">To</th>
                                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Discount %</th>
                                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Cents Discount per order line</th>
                                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Cents Discount per unit</th>
                                                                                <th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Actions</th>
                                                                        </tr>
                                                                </thead>
                                                                <tbody>
                                                                        <tr
                                                                                v-for="qty in quantities"
                                                                                :key="qty.id"
                                                                                class="border-b border-gray-200"
                                                                        >
                                                                                <td class="py-2 px-4">
                                                                                        <input
                                                                                                v-model.number="qty.from"
                                                                                                type="number"
                                                                                                class="w-20 py-1.5 px-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                                        >
                                                                                </td>
                                                                                <td class="py-2 px-4">
                                                                                        <input
                                                                                                v-model.number="qty.to"
                                                                                                type="number"
                                                                                                class="w-20 py-1.5 px-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                                        >
                                                                                </td>
                                                                                <td class="py-2 px-4">
                                                                                        <input
                                                                                                v-model.number="qty.relative"
                                                                                                type="number"
                                                                                                class="w-20 py-1.5 px-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                                        >
                                                                                </td>
                                                                                <td class="py-2 px-4">
                                                                                        <input
                                                                                                v-model.number="qty.absolute"
                                                                                                type="number"
                                                                                                class="w-20 py-1.5 px-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                                        >
                                                                                </td>
                                                                                <td class="py-2 px-4">
                                                                                        <input
                                                                                                v-model.number="qty.absolute_q"
                                                                                                type="number"
                                                                                                class="w-20 py-1.5 px-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                                        >
                                                                                </td>
                                                                                <td class="py-2 px-4 text-right">
                                                                                        <div class="flex justify-end gap-2">
                                                                                                <button
                                                                                                        type="button"
                                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                                        title="Save tier"
                                                                                                        @click="saveQuantity(qty)"
                                                                                                >
                                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                                                                        </svg>
                                                                                                </button>
                                                                                                <button
                                                                                                        type="button"
                                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600"
                                                                                                        title="Delete tier"
                                                                                                        @click="confirmDeleteQuantity(qty)"
                                                                                                >
                                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                                        </svg>
                                                                                                </button>
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                </tbody>
                                                        </table>
                                                </div>

                                                <!-- Add New Tier Form -->
                                                <div
                                                        v-if="showAddQuantityRow"
                                                        class="mt-4 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300"
                                                >
                                                        <h4 class="text-sm font-semibold text-gray-900 m-0 mb-4">Add New Tier</h4>
                                                        <div class="flex flex-wrap gap-4 items-end">
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">From</label>
                                                                        <input
                                                                                v-model.number="newQuantity.from"
                                                                                type="number"
                                                                                class="w-20 py-1.5 px-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                        >
                                                                </div>
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">To</label>
                                                                        <input
                                                                                v-model.number="newQuantity.to"
                                                                                type="number"
                                                                                class="w-20 py-1.5 px-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                        >
                                                                </div>
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Discount %</label>
                                                                        <input
                                                                                v-model.number="newQuantity.relative"
                                                                                type="number"
                                                                                class="w-20 py-1.5 px-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                        >
                                                                </div>
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Cents/Order Line</label>
                                                                        <input
                                                                                v-model.number="newQuantity.absolute"
                                                                                type="number"
                                                                                class="w-24 py-1.5 px-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                        >
                                                                </div>
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Cents/Unit</label>
                                                                        <input
                                                                                v-model.number="newQuantity.absolute_q"
                                                                                type="number"
                                                                                class="w-24 py-1.5 px-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                        >
                                                                </div>
                                                                <div class="flex gap-2">
                                                                        <button
                                                                                type="button"
                                                                                class="py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer hover:bg-orange-6"
                                                                                @click="addQuantity"
                                                                        >
                                                                                Add
                                                                        </button>
                                                                        <button
                                                                                type="button"
                                                                                class="py-2 px-4 bg-white border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
                                                                                @click="showAddQuantityRow = false"
                                                                        >
                                                                                Cancel
                                                                        </button>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>

                                        <!-- Valid Offerings Tab -->
                                        <div v-if="activeTab === 'offerings'">
                                                <h3 class="text-base font-semibold text-gray-900 m-0 mb-2">Valid Offerings</h3>
                                                <p class="text-sm text-gray-500 m-0 mb-6">
                                                        Select the offerings that this bulk discount applies to.
                                                </p>

                                                <div v-if="offerings.length === 0" class="text-center py-12 px-4 bg-gray-50 rounded-lg">
                                                        <p class="text-gray-500 text-sm m-0">No offerings available</p>
                                                </div>

                                                <DualListSelector
                                                        v-else
                                                        :all-items="offerings"
                                                        :selected-ids="selectedOfferingsMap"
                                                        item-label="offerings"
                                                        display-field="name"
                                                        :secondary-field="['variantname', 'size']"
                                                        group-name="bulk-offerings"
                                                        @select="onOfferingSelect"
                                                        @deselect="onOfferingDeselect"
                                                />
                                        </div>
                                </div>
                        </div>
                </main>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import BulkService from 'services/BulkService';
import BulkQuantityService from 'services/BulkQuantityService';
import ProductGroupService from 'services/ProductGroupService';
import OfferingService from 'services/OfferingService';
import BulkOfferingService from 'services/BulkOfferingService';
import DualListSelector from '@/components/DualListSelector.vue';
import type { BulkModel, BulkQuantityModel, ProductGroupModel, OfferingModel } from '@sosocio/creator/interfaces';

export default defineComponent({
        name: 'EditBulkDiscountPage',
        components: {
                DualListSelector,
        },
        setup() {
                const route = useRoute();
                const router = useRouter();
                
                const routeId = computed(() => Number(route.params.id));
                
                const bulkDiscount = ref<BulkModel | null>(null);
                const quantities = ref<BulkQuantityModel[]>([]);
                const productGroups = ref<ProductGroupModel[]>([]);
                const offerings = ref<OfferingModel[]>([]);
                const selectedOfferings = ref<number[]>([]);
                const isLoading = ref(true);
                const isSaving = ref(false);
                const activeTab = ref('properties');
                const showAddQuantityRow = ref(false);
                
                const editData = ref({
                        name: '',
                        count: 'pages' as 'pages' | 'products',
                        active: 1 as number,
                });
                
                const newQuantity = ref({
                        from: 0,
                        to: 0,
                        relative: 0,
                        absolute: 0,
                        absolute_q: 0,
                });
                
                const tabs = [
                        { id: 'properties', label: 'Properties' },
                        { id: 'quantities', label: 'Quantity Tiers' },
                        { id: 'offerings', label: 'Valid Offerings' },
                ];
                
                const hasChanges = computed(() => {
                        if (!bulkDiscount.value) return false;
                        return (
                                editData.value.name !== bulkDiscount.value.name ||
                                editData.value.count !== bulkDiscount.value.count ||
                                editData.value.active !== bulkDiscount.value.active
                        );
                });
                
                const selectedOfferingsMap = computed(() => {
                        const map: Record<number, boolean> = {};
                        selectedOfferings.value.forEach(id => {
                                map[id] = true;
                        });
                        return map;
                });
                
                const showToast = (message: string, variant: 'success' | 'error' = 'success') => {
                        Swal.fire({
                                toast: true,
                                position: 'top-end',
                                icon: variant,
                                title: message,
                                showConfirmButton: false,
                                timer: 3000,
                                timerProgressBar: true,
                        });
                };
                
                const goBack = () => {
                        router.push('/discount-bulk');
                };
                
                const loadData = async () => {
                        isLoading.value = true;
                        try {
                                const id = routeId.value;
                                const params = new URLSearchParams({ limit: '0' });
                                
                                const [discountData, quantitiesData, groupsData, offeringsData, bulkOfferingsData] = await Promise.all([
                                        BulkService.get(id),
                                        BulkService.getAllBulkQuantity(Number(id), params),
                                        ProductGroupService.getAll(params),
                                        OfferingService.getAll(params),
                                        BulkService.getBulkOfferings(Number(id), params),
                                ]);
                                
                                bulkDiscount.value = discountData;
                                editData.value = {
                                        name: discountData.name,
                                        count: discountData.count,
                                        active: discountData.active,
                                };
                                quantities.value = quantitiesData;
                                productGroups.value = groupsData;
                                offerings.value = offeringsData;
                                selectedOfferings.value = bulkOfferingsData.map((bo: any) => bo.offeringid);
                        } catch (error: any) {
                                console.error('Failed to load bulk discount:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to load bulk discount',
                                        icon: 'error',
                                }).then(() => {
                                        router.push('/discount-bulk');
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };
                
                const saveChanges = async () => {
                        if (!bulkDiscount.value || !hasChanges.value) return;
                        
                        isSaving.value = true;
                        try {
                                await BulkService.update(bulkDiscount.value.id, {
                                        name: editData.value.name,
                                        count: editData.value.count,
                                        active: editData.value.active,
                                });
                                bulkDiscount.value = {
                                        ...bulkDiscount.value,
                                        name: editData.value.name,
                                        count: editData.value.count,
                                        active: editData.value.active,
                                };
                                showToast('Bulk discount updated successfully');
                        } catch (error: any) {
                                console.error('Failed to update bulk discount:', error);
                                showToast(error.message || 'Failed to update bulk discount', 'error');
                        } finally {
                                isSaving.value = false;
                        }
                };
                
                const openAddQuantityRow = () => {
                        newQuantity.value = {
                                from: 0,
                                to: 0,
                                relative: 0,
                                absolute: 0,
                                absolute_q: 0,
                        };
                        showAddQuantityRow.value = true;
                };
                
                const addQuantity = async () => {
                        try {
                                const id = routeId.value;
                                const created = await BulkQuantityService.create({
                                        ...newQuantity.value,
                                        bulkid: Number(id),
                                });
                                quantities.value.push(created);
                                showAddQuantityRow.value = false;
                                showToast('Quantity tier added successfully');
                        } catch (error: any) {
                                console.error('Failed to add quantity tier:', error);
                                showToast(error.message || 'Failed to add quantity tier', 'error');
                        }
                };
                
                const saveQuantity = async (qty: BulkQuantityModel) => {
                        try {
                                const id = routeId.value;
                                await BulkQuantityService.update(qty.id, {
                                        from: qty.from,
                                        to: qty.to,
                                        relative: qty.relative,
                                        absolute: qty.absolute,
                                        absolute_q: qty.absolute_q,
                                        bulkid: Number(id),
                                });
                                showToast('Quantity tier saved successfully');
                        } catch (error: any) {
                                console.error('Failed to save quantity tier:', error);
                                showToast(error.message || 'Failed to save quantity tier', 'error');
                        }
                };
                
                const confirmDeleteQuantity = async (qty: BulkQuantityModel) => {
                        const result = await Swal.fire({
                                title: 'Delete Quantity Tier?',
                                text: 'Are you sure you want to delete this tier?',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                confirmButtonText: 'Delete',
                        });
                        
                        if (result.isConfirmed) {
                                try {
                                        await BulkQuantityService.delete(qty.id);
                                        quantities.value = quantities.value.filter(q => q.id !== qty.id);
                                        showToast('Quantity tier deleted successfully');
                                } catch (error: any) {
                                        console.error('Failed to delete quantity tier:', error);
                                        showToast(error.message || 'Failed to delete quantity tier', 'error');
                                }
                        }
                };
                
                const onOfferingSelect = async (offering: OfferingModel) => {
                        try {
                                const id = routeId.value;
                                await BulkOfferingService.create({
                                        bulkid: Number(id),
                                        offeringid: offering.id,
                                        groupid: offering.groupid,
                                        variantid: offering.variantid,
                                        typeid: offering.typeid,
                                } as any);
                                selectedOfferings.value.push(offering.id);
                                showToast('Offering added');
                        } catch (error: any) {
                                console.error('Failed to add offering:', error);
                                showToast(error.message || 'Failed to add offering', 'error');
                        }
                };
                
                const onOfferingDeselect = async (offering: OfferingModel) => {
                        try {
                                const id = routeId.value;
                                const params = new URLSearchParams({
                                        where: JSON.stringify({ bulkid: Number(id), offeringid: offering.id }),
                                });
                                await BulkOfferingService.deleteWhere(params);
                                selectedOfferings.value = selectedOfferings.value.filter(oid => oid !== offering.id);
                                showToast('Offering removed');
                        } catch (error: any) {
                                console.error('Failed to remove offering:', error);
                                showToast(error.message || 'Failed to remove offering', 'error');
                        }
                };
                
                onMounted(() => {
                        loadData();
                });
                
                return {
                        routeId,
                        bulkDiscount,
                        quantities,
                        productGroups,
                        offerings,
                        selectedOfferings,
                        selectedOfferingsMap,
                        isLoading,
                        isSaving,
                        activeTab,
                        tabs,
                        editData,
                        hasChanges,
                        showAddQuantityRow,
                        newQuantity,
                        goBack,
                        saveChanges,
                        openAddQuantityRow,
                        addQuantity,
                        saveQuantity,
                        confirmDeleteQuantity,
                        onOfferingSelect,
                        onOfferingDeselect,
                };
        },
});
</script>
